import {priority} from '@priority-software/priority-proto-api/lib';
import {PatchSavedSearch, PatchFilter} from '../index';
import {patchFilterToStore, removeFiltersWithoutValue} from '../../../../actions/utils/filter';
import {Form} from '../../../../../../stores/Form/Form';
import {SavedSearch} from '../../../../types';
import {createNewFilterCondition, patchFetchConditions} from '../../../../utils/filters';
import {texts} from '../texts';
import {Column} from '../../../../../../stores/Form';
import {AISuggestedFilter} from '../../../../../../services/rest/llm/aiSuggestedFilters';
import {DateRangeID} from '../../../../../../common/dateRange';
import {Operator} from '../../../../../../_legacy/Searches/consts/operators';
import {ApiOperatorMapping} from '../../../../../AppSearches/types/operator';
import {currentUserPlaceholder} from '../../../../../../services/rest/llm/constants';
import {getUsername} from '../../../../../../_shared/GWTBridge/invoke/genPage';
import {serializeFilterValues} from '../../../../common/serializeFilterValues';
import {formatDate} from '../../../../utils/date';
import {getUuid4} from '../../../../../../utils/getUuid4';

export const convertSearchToStore = (search: PatchSavedSearch, filters: PatchFilter[]) => {
    const storeFilters = filters.map(filter => ({
        ...patchFilterToStore(filter),
        sort: filter.sort
    }));

    return {
        ...search,
        filters: storeFilters
    };
};

export const createNewFilter = (column: Column) => {
    return {
        Condition: patchFetchConditions(column),
        Operator: priority.netitems.shared.LogicalOperator.Or,
        sort: null
    };
};

export const patchSavedSearch = (search: SavedSearch, form: Form): PatchSavedSearch => {
    /*
     * Currently we support only the same ignoreCase vale for all filters regardless
     * the API supports different values for each filter
     */

    const ignoreCase = search.filters[0]?.Condition?.[0]?.IgnoreCase;

    const newFilter = search.filters.map(filter => {
        const {ColumnName, ColumnID} = filter.Condition[0] || {};
        /*
         * !!! It's temporary solution, some filters may not have ColumnName field,
         * instead they have ColumnID (i.e. filters from the form search mode). Need more investigation.
         */
        const column = ColumnName
            ? form.getColumnByName(ColumnName)
            : form.getColumnByID(ColumnID as number);

        const patchFilter: PatchFilter = {
            ...filter,
            Condition: patchFetchConditions(column, filter)
        };
        return patchFilter;
    });

    return {...search, ignoreCase, filters: newFilter};
};

export const prepareSearchToStore = (search: PatchSavedSearch) => {
    const filteredData = removeFiltersWithoutValue(search.filters);
    return filteredData ? convertSearchToStore(search, filteredData) : undefined;
};

export const createNewSearch = (column: Column | null, index: number): PatchSavedSearch => {
    return {
        filters: column
            ? [
                  {
                      Condition: patchFetchConditions(column),
                      Operator: priority.netitems.shared.LogicalOperator.Or,
                      sort: null
                  }
              ]
            : [],
        filterOp: priority.netitems.shared.LogicalOperator.And,
        index,
        ignoreCase: true,
        isDefault: false,
        isSaved: false,
        isConverted: false,
        name: texts.newSearch,
        id: getUuid4()
    };
};

export async function setAIFilterValues(
    setFilters: (filters: PatchFilter[]) => void,
    columns: Array<Column>,
    filters: Array<AISuggestedFilter>
) {
    const data = filters
        .map(item => {
            const column = columns.find(c => c.Name === item.column);
            if (column) {
                const conditionData = mapConditionData(column, item);

                return {
                    Condition: [createNewFilterCondition(column, conditionData)],
                    Operator: priority.netitems.shared.LogicalOperator.Or,
                    sort: null
                };
            }
            return null;
        })
        .filter(Boolean);

    setFilters(data);
}

// const dateOptionValues = Object.values(DateRangeID);
const getDateRangeValue = (value?: string) => {
    const dateRangeMap = new Map<string, string>(
        Object.entries(DateRangeID).map(([key, value]) => [key.toLowerCase(), value])
    );

    return dateRangeMap.get(value?.toLowerCase().replace(/_/g, ''));
};

export const mapConditionData = (column: Column, item: AISuggestedFilter) => {
    const data = {
        Operator: ApiOperatorMapping[Operator[item.operator]],
        DateEnum: [],
        Values: serializeFilterValues(Operator[item.operator], item.values).map(value => {
            return value === currentUserPlaceholder ? getUsername() : value;
        })
    };

    if (
        column.Type === priority.netitems.form.ColumnType.Date8 ||
        column.Type === priority.netitems.form.ColumnType.Date10 ||
        column.Type === priority.netitems.form.ColumnType.Date14 ||
        column.Type === priority.netitems.form.ColumnType.Time5
    ) {
        data.Values = [];
        item.values.forEach((value, i) => {
            const enumValue = getDateRangeValue(value);

            if (enumValue) {
                data.DateEnum.push(enumValue);
                data.Values.push('');
            } else {
                const formattedDate = value ? formatDate(new Date(value), column.Type) : '';
                data.Values.push(formattedDate);
                data.DateEnum.push('');
            }
        });
    } else if (column.Type === priority.netitems.form.ColumnType.Boolean) {
        data.Values = item.values.map(value => {
            return value === 'true' ? 'Y' : '';
        });
    }

    return data;
};

export const isSearchDisabled = (search?: PatchSavedSearch) => {
    if (!search) return true;

    if (search?.filters?.length > 0) {
        return search.filters.some(filter => {
            if (filter.Condition?.length > 0) {
                return filter.Condition.some(condition => {
                    return !(
                        condition.operator === Operator.Empty ||
                        condition.operator === Operator.NotEmpty ||
                        condition.dateEnum?.length > 0 ||
                        condition.values?.length > 0
                    );
                });
            }
            return true;
        });
    }

    return true;
};
